import { ORDER_STAGE_BY_NUMBER } from '../model/stages';
import type { OrderLog } from '../model/order-schema';

/**
 * Status-history derivation, ported from cpanel `StatusHistory`. The backend
 * sends a flat `logs[]`; the timeline needs (a) the stage-transition entries (a
 * log whose `changed_values.stage.new` set a new stage) and (b) the remaining
 * "edit" entries, interleaved in chronological order. All JSON parsing of the
 * `changed_values` string is contained here so the UI never touches raw JSON.
 */

export type ParsedChangedValues = Record<string, unknown>;

/** Tolerant `changed_values` JSON parse — returns `{}` on malformed input. */
export function parseChangedValues(raw: string | null | undefined): ParsedChangedValues {
  if (!raw) return {};
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return {};
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? (parsed as ParsedChangedValues) : {};
  } catch {
    return {};
  }
}

/** Extract the new stage number a log set, if any (`changed_values.stage.new`). */
function stageNewFromLog(log: OrderLog): number | null {
  const changed = parseChangedValues(log.changed_values);
  const stage = changed.stage;
  if (stage && typeof stage === 'object' && 'new' in stage) {
    const next = (stage as { new?: unknown }).new;
    const num = Number(next);
    return Number.isFinite(num) ? num : null;
  }
  return null;
}

/** Source attribution string (cpanel: `fullname (role)` | `fullname` | system). */
export function logSource(log: OrderLog): { name: string; role: string } {
  const name = (log.fullname ?? '').trim();
  const role = (log.role ?? '').trim();
  return { name, role };
}

export type HistoryItemType = 'stage' | 'edit';

export interface StageHistoryItem {
  type: 'stage';
  stage: number;
  /** Russian stage label (UI prefers the i18n key from `stageKey`). */
  name: string;
  color: string;
  isActive: boolean;
  createdAt: string | null;
  source: { name: string; role: string };
  changedValues: ParsedChangedValues | null;
}

export interface EditHistoryItem {
  type: 'edit';
  createdAt: string | null;
  role: string;
  source: { name: string; role: string };
  changedValues: ParsedChangedValues;
}

export type HistoryItem = StageHistoryItem | EditHistoryItem;

function logTime(log: OrderLog): number {
  return log.created_at ? new Date(log.created_at).getTime() : 0;
}

/**
 * Build the ordered status-history timeline from `logs[]`.
 *
 * - Stage items: one per stage number, taken from the first log that set it.
 *   Stages with no log are omitted (the UI may show the full ladder greyed, but
 *   we only surface stages that actually occurred — cpanel's "active" set).
 * - Edit items: every non-stage log, slotted in chronological position.
 *
 * The result is sorted ascending by time so the UI renders top-to-bottom oldest
 * → newest with gradient connectors.
 */
export function buildStatusHistory(logs: OrderLog[] | null | undefined): HistoryItem[] {
  if (!logs || logs.length === 0) return [];

  const sorted = [...logs].sort((a, b) => logTime(a) - logTime(b));

  const stageLogIds = new Set<string | number>();
  const stageItems: StageHistoryItem[] = [];
  const seenStages = new Set<number>();

  for (const log of sorted) {
    const stageNew = stageNewFromLog(log);
    if (stageNew == null || seenStages.has(stageNew)) continue;
    seenStages.add(stageNew);
    if (log.id != null) stageLogIds.add(log.id);
    const meta = ORDER_STAGE_BY_NUMBER.get(stageNew);
    stageItems.push({
      type: 'stage',
      stage: stageNew,
      name: meta?.name ?? `stage_${stageNew}`,
      color: meta?.color ?? '#536088',
      isActive: true,
      createdAt: log.created_at ?? null,
      source: logSource(log),
      changedValues: parseChangedValues(log.changed_values),
    });
  }

  const editItems: EditHistoryItem[] = sorted
    .filter((log) => log.id == null || !stageLogIds.has(log.id))
    .map((log) => ({
      type: 'edit',
      createdAt: log.created_at ?? null,
      role: (log.role ?? '').trim(),
      source: logSource(log),
      changedValues: parseChangedValues(log.changed_values),
    }));

  const toTime = (iso: string | null): number => (iso ? new Date(iso).getTime() : 0);

  return [...stageItems, ...editItems].sort(
    (a, b) => toTime(a.createdAt) - toTime(b.createdAt),
  );
}

export interface ChangeDiffRow {
  field: string;
  old: string;
  new: string;
}

/** Render one `changed_values` field value to a flat display string. */
function stringifyValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[object]';
    }
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '—';
}

/**
 * Flatten a parsed `changed_values` map to `{field, old, new}` rows for the
 * change-diff dialog. Each value is expected to be `{old, new}`; bare values are
 * shown as a single new value.
 */
export function changeDiffRows(changed: ParsedChangedValues): ChangeDiffRow[] {
  return Object.entries(changed).map(([field, value]) => {
    if (value && typeof value === 'object' && ('old' in value || 'new' in value)) {
      const pair = value as { old?: unknown; new?: unknown };
      return {
        field,
        old: stringifyValue(pair.old),
        new: stringifyValue(pair.new),
      };
    }
    return { field, old: '—', new: stringifyValue(value) };
  });
}
